import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useWorld } from '@/hooks/useWorld'
import { useNationsByWorld } from '@/hooks/useNationsByWorld'
import { AppLayout } from '@/components/layout/AppLayout'
import { AdminOnly } from '@/components/AdminOnly'
import { NationCard } from '@/components/nation/NationCard'
import { NationCardSkeleton } from '@/components/nation/NationCardSkeleton'
import { WorldStatusControl } from '@/components/world/WorldStatusControl'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { WorldStatus } from '@/types'

const statusConfig: Record<WorldStatus, { label: string; className: string }> = {
  setup: {
    label: 'Setup',
    className: 'bg-gray-100 text-gray-800 border-gray-200',
  },
  active: {
    label: 'Active',
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  paused: {
    label: 'Paused',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  completed: {
    label: 'Completed',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function WorldDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { world, isLoading: worldLoading, error: worldError } = useWorld(id)
  const { nations, isLoading: nationsLoading, error: nationsError } = useNationsByWorld(id)

  const isLoading = worldLoading
  const error = worldError

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name })
    }
    return items
  }, [world])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {worldError?.message === 'JSON object requested, multiple (or no) rows returned'
            ? 'World not found.'
            : 'Failed to load world. Please try again later.'}
        </div>
      )}

      {isLoading && (
        <div className="space-y-6">
          <div>
            <div className="bg-muted h-8 w-48 animate-pulse rounded" />
            <div className="bg-muted mt-2 h-5 w-64 animate-pulse rounded" />
          </div>
          <div className="border-t pt-6">
            <div className="bg-muted h-6 w-24 animate-pulse rounded" />
            <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <NationCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      )}

      {!isLoading && !error && world && (
        <>
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <h1 className="text-3xl font-bold">{world.name}</h1>
              <AdminOnly
                fallback={
                  <Badge className={cn(statusConfig[world.status].className)}>
                    {statusConfig[world.status].label}
                  </Badge>
                }
              >
                <WorldStatusControl worldId={world.id} currentStatus={world.status} />
              </AdminOnly>
            </div>
            <div className="text-muted-foreground mt-2 flex flex-wrap gap-4 text-sm">
              <span>
                <span className="font-medium">Tick:</span> {world.current_tick}
              </span>
              <span>
                <span className="font-medium">Interval:</span> {world.tick_interval_hours}h
              </span>
              <span>
                <span className="font-medium">Created:</span> {formatDate(world.created_at)}
              </span>
            </div>
          </div>

          <div className="border-t pt-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Nations</h2>
              <AdminOnly>
                <Button onClick={() => navigate(`/worlds/${id}/nations/new`)}>Add Nation</Button>
              </AdminOnly>
            </div>

            {nationsError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
                Failed to load nations. Please try again later.
              </div>
            )}

            {nationsLoading && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <NationCardSkeleton key={i} />
                ))}
              </div>
            )}

            {!nationsLoading && !nationsError && nations && nations.length === 0 && (
              <div className="rounded-lg border border-dashed p-12 text-center">
                <h3 className="text-lg font-medium">No nations yet</h3>
                <p className="text-muted-foreground mt-1">
                  This world doesn't have any nations yet.
                </p>
                <AdminOnly>
                  <Button className="mt-4" onClick={() => navigate(`/worlds/${id}/nations/new`)}>
                    Create the first nation
                  </Button>
                </AdminOnly>
              </div>
            )}

            {!nationsLoading && !nationsError && nations && nations.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {nations.map((nation) => (
                  <NationCard key={nation.id} nation={nation} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </AppLayout>
  )
}
